import { GradeLozenge } from "@idwal/idwal-react-components";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { ProgressSpinner } from "primereact/progressspinner";
import "./GradingInput.css";

export interface GradingInputProps {
    /** Section grade on the 0–100 UI scale, or null when the section is
     *  ungraded (the field renders blank, not zero). */
    value: number | null;
    /** Emits the new grade — snapped to the nearest increment of 10 — or null
     *  when the field is cleared (reverting the section to ungraded). */
    onChange: (value: number | null) => void;
    /** When true, render a read-only lozenge instead of an editable input
     *  (e.g. the inspection has reached Full Report status). */
    readOnly?: boolean;
    /** Shows a spinner while a save/load is in flight for this row. */
    loading?: boolean;
    /** Associates an external <label htmlFor> with the input for accessibility. */
    inputId?: string;
}

export const GRADE_STEP = 10;
export const GRADE_MIN = 0;
export const GRADE_MAX = 100;

/**
 * Snap a value to the nearest allowed grade increment and clamp to 0–100.
 * A cleared field (null/undefined) stays ungraded rather than snapping to 0,
 * which keeps "ungraded" distinct from a genuine grade of 0.
 */
export const snapToGradeStep = (value: number | null | undefined): number | null => {
    if (value === null || value === undefined) {
        return null;
    }
    const snapped = Math.round(value / GRADE_STEP) * GRADE_STEP;
    return Math.min(GRADE_MAX, Math.max(GRADE_MIN, snapped));
};

export const GradingInput = ({ value, onChange, readOnly, loading, inputId }: GradingInputProps) => {
    if (loading) {
        return <ProgressSpinner style={{ width: "20px", height: "20px" }} />;
    }

    if (readOnly) {
        // An ungraded section has no grade to render as a lozenge, so show a
        // neutral placeholder. (Final read-only treatment pending design.)
        if (value === null) {
            return (
                <span className="grading-input-ungraded" data-testid="grading-input-ungraded">
                    —
                </span>
            );
        }
        return <GradeLozenge grade={value} />;
    }

    const handleChange = (event: InputNumberValueChangeEvent) => {
        onChange(snapToGradeStep(event.value));
    };

    return (
        <InputNumber
            inputId={inputId}
            value={value}
            onValueChange={handleChange}
            showButtons
            min={GRADE_MIN}
            max={GRADE_MAX}
            step={GRADE_STEP}
            allowEmpty
            useGrouping={false}
        />
    );
};
